// components/landing/SystemFlowSection.tsx
'use client';

import React, { useCallback, useState } from 'react';
import { ReactFlow, Node, Edge, Controls, Background, useNodesState, useEdgesState, MarkerType, BackgroundVariant } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Network, Cpu, Database, Cloud, ChevronRight } from 'lucide-react';

// 커스텀 노드 스타일 정의
const nodeStyles = {
  sensor: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #764ba2',
    borderRadius: '12px',
    padding: '16px',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  actuator: {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    color: 'white',
    border: '2px solid #f5576c',
    borderRadius: '12px',
    padding: '16px',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  controller: {
    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    color: 'white',
    border: '2px solid #00f2fe',
    borderRadius: '12px',
    padding: '20px',
    fontSize: '16px',
    fontWeight: '700',
    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
  },
  cloud: {
    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    color: 'white',
    border: '2px solid #38f9d7',
    borderRadius: '12px',
    padding: '20px',
    fontSize: '16px',
    fontWeight: '700',
    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
  },
};

const initialNodes: Node[] = [
  {
    id: '1',
    type: 'default',
    data: { label: '🌡️ 온도 센서' },
    position: { x: 50, y: 50 },
    style: nodeStyles.sensor,
  },
  {
    id: '2',
    type: 'default',
    data: { label: '💧 습도 센서' },
    position: { x: 50, y: 150 },
    style: nodeStyles.sensor,
  },
  {
    id: '3',
    type: 'default',
    data: { label: '☀️ 조도 센서' },
    position: { x: 50, y: 250 },
    style: nodeStyles.sensor,
  },
  {
    id: '4',
    type: 'default',
    data: { label: '🎛️ ESP32 제어기' },
    position: { x: 300, y: 150 },
    style: nodeStyles.controller,
  },
  {
    id: '5',
    type: 'default',
    data: { label: '💡 LED 조명' },
    position: { x: 550, y: 50 },
    style: nodeStyles.actuator,
  },
  {
    id: '6',
    type: 'default',
    data: { label: '💦 관수 펌프' },
    position: { x: 550, y: 150 },
    style: nodeStyles.actuator,
  },
  {
    id: '7',
    type: 'default',
    data: { label: '🌬️ 환풍기' },
    position: { x: 550, y: 250 },
    style: nodeStyles.actuator,
  },
  {
    id: '8',
    type: 'default',
    data: { label: '☁️ 클라우드 서버' },
    position: { x: 300, y: 350 },
    style: nodeStyles.cloud,
  },
];

const initialEdges: Edge[] = [
  {
    id: 'e1-4',
    source: '1',
    target: '4',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#667eea', strokeWidth: 2 },
  },
  {
    id: 'e2-4',
    source: '2',
    target: '4',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#667eea', strokeWidth: 2 },
  },
  {
    id: 'e3-4',
    source: '3',
    target: '4',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#667eea', strokeWidth: 2 },
  },
  {
    id: 'e4-5',
    source: '4',
    target: '5',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#f5576c', strokeWidth: 2 },
  },
  {
    id: 'e4-6',
    source: '4',
    target: '6',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#f5576c', strokeWidth: 2 },
  },
  {
    id: 'e4-7',
    source: '4',
    target: '7',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#f5576c', strokeWidth: 2 },
  },
  {
    id: 'e4-8',
    source: '4',
    target: '8',
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#43e97b', strokeWidth: 2 },
  },
];

export default function SystemFlowSection() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  return (
    <section className="relative bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-purple-900/20 overflow-hidden py-24 lg:py-32">
      {/* 배경 패턴 */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 왼쪽: 설명 */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-700 dark:text-purple-300 text-sm font-medium mb-6">
              <Network className="w-4 h-4 mr-2" />
              시스템 구성
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              직관적인
              <span className="block text-purple-600 dark:text-purple-400 mt-2">시스템 설계</span>
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              드래그 앤 드롭으로 센서와 액추에이터를 연결하세요. 복잡한 농장 시스템도 시각적으로 쉽게 구성할 수 있습니다.
            </p>

            {/* 기능 리스트 */}
            <div className="space-y-4 mb-8">
              {[
                {
                  icon: Cpu,
                  title: '센서 통합',
                  desc: '다양한 IoT 센서 지원',
                },
                {
                  icon: Database,
                  title: '데이터 흐름',
                  desc: '실시간 데이터 파이프라인',
                },
                {
                  icon: Cloud,
                  title: '클라우드 연동',
                  desc: '자동 동기화 및 백업',
                },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Icon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">{item.title}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1">
              구성 시작하기
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* 오른쪽: React Flow */}
          <div className="order-1 lg:order-2">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 h-[500px]">
              <div className="h-full rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  fitView
                  attributionPosition="bottom-left"
                  className="bg-gray-50 dark:bg-gray-900"
                >
                  <Background variant={BackgroundVariant.Dots} gap={16} size={1} className="dark:opacity-20" />
                  <Controls className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg" />
                </ReactFlow>
              </div>
              <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">노드를 드래그하여 이동할 수 있습니다</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
